import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Plus,
  Settings2,
  Users,
  RefreshCw,
  Check,
  History,
  DollarSign,
  Infinity as InfinityIcon,
  ChevronDown,
  ChevronUp,
  Save,
  RotateCcw,
  AlertTriangle,
  PenTool,
  FolderOpen,
  Palette,
  Code,
  BarChart3,
  Shield,
  MessageCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { UsagePricesEditor } from "@/components/admin/UsagePricesEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { customPlanApi, adminFeaturesApi } from "@/services/api";

// Feature categories for grouping
const FEATURE_CATEGORIES = {
  signing: { name: "Signing", icon: PenTool },
  organisation: { name: "Organization", icon: FolderOpen },
  branding: { name: "Branding", icon: Palette },
  developer: { name: "Developer", icon: Code },
  analytics: { name: "Analytics", icon: BarChart3 },
  security: { name: "Security", icon: Shield },
  team: { name: "Team", icon: Users },
  support: { name: "Support", icon: MessageCircle },
};

// Collapsible Section Component
function CollapsibleSection({
  title,
  icon: Icon,
  count,
  total,
  children,
  defaultOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <span className="flex items-center gap-2 font-medium">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />} {title}
          <Badge variant="secondary" className="ml-2">
            {count}/{total}
          </Badge>
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {isOpen && <div className="px-3 pb-3 border-t">{children}</div>}
    </div>
  );
}

export default function AdminCustomPlans() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Selected organisation for editing
  const [selectedOrg, setSelectedOrg] = useState(null);

  // New custom plan dialog
  const [newPlanDialog, setNewPlanDialog] = useState(false);
  const [searchOrg, setSearchOrg] = useState("");
  const [targetOrg, setTargetOrg] = useState(null);

  // Form state for new/edit custom plan
  const [formPlanName, setFormPlanName] = useState("");
  const [formFeatures, setFormFeatures] = useState({});
  const [formLimits, setFormLimits] = useState({});
  const [formPrice, setFormPrice] = useState("");
  const [formBillingInterval, setFormBillingInterval] = useState("month");
  const [formBillingType, setFormBillingType] = useState("fixed");
  const [formExpiresAt, setFormExpiresAt] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formReason, setFormReason] = useState("");

  // Dialog states
  const [historyDialog, setHistoryDialog] = useState(false);
  const [revertDialog, setRevertDialog] = useState(false);
  const [revertPlan, setRevertPlan] = useState("professional");
  const [revertReason, setRevertReason] = useState("");

  // Query: Fetch options (features and limits definitions)
  const { data: options = { features: [], limits: [] } } = useQuery({
    queryKey: ["customPlan", "options"],
    queryFn: async () => {
      const response = await customPlanApi.getOptions();
      if (response.data.success) {
        return response.data.data;
      }
      return { features: [], limits: [] };
    },
  });

  // Query: Fetch organisations with custom plans
  const { data: customPlanOrgs = [], isLoading: loading } = useQuery({
    queryKey: ["customPlan", "organisations"],
    queryFn: async () => {
      const response = await customPlanApi.listOrganizations();
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    },
  });

  // Query: Fetch all organisations for search
  const { data: allOrganizations = [] } = useQuery({
    queryKey: ["admin", "allOrganizations"],
    queryFn: async () => {
      const response = await adminFeaturesApi.listOrganizations({
        limit: 1000,
      });
      if (response.data.success) {
        return response.data.data.organisations || [];
      }
      return [];
    },
  });

  // Query: Fetch organisation details (only when selectedOrg exists)
  const { data: orgDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ["customPlan", "details", selectedOrg?.id],
    queryFn: async () => {
      const response = await customPlanApi.getDetails(selectedOrg.id);
      if (response.data.success) {
        const { data } = response.data;
        // Populate form with current values
        setFormPlanName(data.planName || "");
        // featureFlags, not featureOverrides: the latter is the stored shape
        // ({ enabled, ... }) and every object is truthy, which made a disabled
        // feature render as ON and spring back after each save.
        setFormFeatures(data.featureFlags || {});
        setFormLimits(data.limitsOverrides || {});
        setFormPrice(data.price ? (data.price / 100).toString() : "");
        setFormBillingInterval(data.billingInterval || "month");
        setFormBillingType(data.billingType || "fixed");
        setFormExpiresAt(data.expiresAt ? data.expiresAt.split("T")[0] : "");
        setFormNotes(data.notes || "");
        return data;
      }
      return null;
    },
    enabled: !!selectedOrg?.id,
  });

  // Query: Fetch history (only when historyDialog is open)
  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ["customPlan", "history", selectedOrg?.id],
    queryFn: async () => {
      const response = await customPlanApi.getHistory(selectedOrg.id);
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    },
    enabled: !!selectedOrg?.id && historyDialog,
  });

  // Mutation: Save custom plan
  const saveMutation = useMutation({
    mutationFn: ({ orgId, data }) => customPlanApi.setCustomPlan(orgId, data),
    onSuccess: (response, { orgId, isNewPlan }) => {
      if (response.data.success) {
        toast.success(t("toast.customPlanSaved"));
        queryClient.invalidateQueries({
          queryKey: ["customPlan", "organisations"],
        });

        if (isNewPlan) {
          setNewPlanDialog(false);
          resetForm();
        } else {
          queryClient.invalidateQueries({
            queryKey: ["customPlan", "details", orgId],
          });
        }
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save custom plan");
    },
  });

  // Mutation: Revert to standard plan
  const revertMutation = useMutation({
    mutationFn: () =>
      customPlanApi.revertToStandard(
        selectedOrg.id,
        revertPlan,
        revertReason || "Reverted by admin",
      ),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success(`Organization reverted to ${revertPlan} plan`);
        setRevertDialog(false);
        closeDetails();
        queryClient.invalidateQueries({
          queryKey: ["customPlan", "organisations"],
        });
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to revert plan");
    },
  });

  const saving = saveMutation.isPending || revertMutation.isPending;

  // Open organisation for editing
  const openOrgDetails = (org) => {
    setSelectedOrg(org);
  };

  // Close details
  const closeDetails = () => {
    setSelectedOrg(null);
    resetForm();
  };

  // Reset form
  const resetForm = () => {
    setFormPlanName("");
    setFormFeatures({});
    setFormLimits({});
    setFormPrice("");
    setFormBillingInterval("month");
    setFormExpiresAt("");
    setFormNotes("");
    setFormReason("");
    setTargetOrg(null);
  };

  // Open new custom plan dialog
  const openNewPlanDialog = () => {
    resetForm();
    setNewPlanDialog(true);
  };

  // Toggle feature in form.
  //
  // Switching one off zeroes every limit it governs, so the form shows what the
  // API will actually store (customPlan.service applies the same rule).
  const toggleFeature = (feature) => {
    setFormFeatures((prev) => {
      const next = { ...prev, [feature]: !prev[feature] };
      if (next[feature] === false) {
        const governed = (options.limits || [])
          .filter((l) => l.requiresFeature === feature)
          .map((l) => l.key);
        if (governed.length > 0) {
          setFormLimits((limitsPrev) => {
            const updated = { ...limitsPrev };
            for (const key of governed) updated[key] = 0;
            return updated;
          });
        }
      }
      return next;
    });
  };

  // A limit is editable only while the feature that governs it is on.
  const isLimitLocked = (limit) =>
    !!limit.requiresFeature && formFeatures[limit.requiresFeature] === false;

  // An unlimited allowance cannot be covered by a fixed fee.
  const hasUnlimitedLimit = Object.values(formLimits).some(
    (v) => Number(v) === -1,
  );
  const effectiveBillingType =
    formBillingType === "fixed" && hasUnlimitedLimit ? "payg" : formBillingType;

  // Set limit in form
  const setLimit = (key, value) => {
    const numValue = value === "" ? undefined : parseInt(value, 10);
    setFormLimits((prev) => ({
      ...prev,
      [key]: numValue,
    }));
  };

  // Set unlimited (-1)
  // Toggle, not a one-way switch. Setting -1 and leaving no way back stranded
  // the plan on pay-as-you-go: an unlimited limit forces PAYG, so a plan could
  // never be moved to a fixed price once any limit had been set to unlimited.
  const toggleUnlimited = (key) => {
    setFormLimits((prev) => ({
      ...prev,
      [key]: prev[key] === -1 ? undefined : -1,
    }));
  };

  // Save custom plan (create or update)
  const saveCustomPlan = () => {
    const orgId = selectedOrg?.id || targetOrg?.id;
    if (!orgId) {
      toast.error(t("toast.pleaseSelectOrg"));
      return;
    }

    const data = {
      name: formPlanName || "Custom Plan",
      features: formFeatures,
      limits: formLimits,
      price: formPrice ? Math.round(parseFloat(formPrice) * 100) : null,
      billingInterval: formBillingInterval,
      billingType: formBillingType,
      expiresAt: formExpiresAt || null,
      notes: formNotes || null,
      reason: formReason || "Admin configuration",
    };

    saveMutation.mutate({ orgId, data, isNewPlan: !selectedOrg });
  };

  // Open history dialog
  const openHistory = () => {
    if (!selectedOrg) return;
    setHistoryDialog(true);
  };

  // Handle revert
  const handleRevert = () => {
    if (!selectedOrg) return;
    revertMutation.mutate();
  };

  // Refresh data
  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ["customPlan", "organisations"],
    });
  };

  // Filter organisations for search
  const filteredOrgs = useMemo(
    () =>
      allOrganizations.filter(
        (org) =>
          org.plan !== "custom" &&
          (org.name?.toLowerCase().includes(searchOrg.toLowerCase()) ||
            org.id?.toLowerCase().includes(searchOrg.toLowerCase())),
      ),
    [allOrganizations, searchOrg],
  );

  // Group features by category
  const groupedFeatures = useMemo(
    () =>
      options.features.reduce((acc, feature) => {
        const category = feature.category || "other";
        if (!acc[category]) acc[category] = [];
        acc[category].push(feature);
        return acc;
      }, {}),
    [options.features],
  );

  const renderOrganisationLists = () => {
    if (loading) {
      return (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      );
    }
    return customPlanOrgs?.length === 0 ? (
      <div className="text-center py-8 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>{t("admin.noCustomPlans")}</p>
        <Button variant="link" onClick={openNewPlanDialog}>
          Create one now
        </Button>
      </div>
    ) : (
      <div className="space-y-2">
        {customPlanOrgs?.map((org) => (
          <div
            key={org.id}
            className={cn(
              "p-3 rounded-lg border cursor-pointer transition-colors",
              selectedOrg?.id === org.id
                ? "border-primary bg-primary/5"
                : "hover:bg-muted/50",
            )}
            onClick={() => openOrgDetails(org)}
          >
            <div className="flex items-center justify-between">
              <div className="font-medium truncate">{org.name}</div>
              <Badge
                variant="outline"
                className="bg-yellow-50 text-yellow-700 border-yellow-200 truncate max-w-30"
                title={org.planName}
              >
                {org.planName || "Custom"}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Check className="h-3 w-3" />
                {org.featureCount} features
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {org.userCount} users
              </span>
            </div>
            {org.priceDisplay && (
              <div className="mt-1 text-sm font-medium text-green-600">
                ${org.priceDisplay}/{org.billingInterval}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderHistoryContent = () => {
    if (historyLoading) {
      return (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      );
    }
    return history?.length === 0 ? (
      <div className="text-center py-8 text-muted-foreground">
        No history available
      </div>
    ) : (
      <div className="space-y-3">
        {history?.map((entry, idx) => (
          <div key={entry.id || idx} className="p-3 border rounded-lg">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{entry.action}</Badge>
              <span className="text-xs text-muted-foreground">
                {new Date(entry.created_at).toLocaleString()}
              </span>
            </div>
            {entry.reason && <p className="text-sm mt-1">{entry.reason}</p>}
            {entry.admin_email && (
              <p className="text-xs text-muted-foreground mt-1">
                By: {entry.admin_email}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Custom Plans
          </h1>
          <p className="text-muted-foreground">
            Manage custom plans with manually configured features and limits
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openNewPlanDialog}>
            <Plus className="h-4 w-4 mr-2" />
            New Custom Plan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Organisations List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">
              {t("admin.customPlanOrgs")}
            </CardTitle>
            <CardDescription>
              {customPlanOrgs.length} organisation
              {customPlanOrgs.length !== 1 ? "s" : ""} with custom plans
            </CardDescription>
          </CardHeader>
          <CardContent>{renderOrganisationLists()}</CardContent>
        </Card>

        {/* Details/Edit Panel */}
        <Card className="lg:col-span-2">
          {selectedOrg ? (
            <>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {selectedOrg.name}
                      {orgDetails?.planName && (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-700 border-yellow-200"
                        >
                          {orgDetails.planName}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {t("admin.configureFeatures")}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={openHistory}>
                      <History className="h-4 w-4 mr-1" />
                      History
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setRevertDialog(true)}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Revert
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {detailsLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-40 w-full" />
                  </div>
                ) : (
                  <Tabs defaultValue="features">
                    <TabsList className="mb-4">
                      <TabsTrigger value="features">
                        {t("admin.customPlans.features")}
                      </TabsTrigger>
                      <TabsTrigger value="limits">
                        {t("admin.customPlans.limits")}
                      </TabsTrigger>
                      <TabsTrigger value="pricing">Pricing</TabsTrigger>
                      <TabsTrigger value="billing">
                        {t("billing.title")}
                      </TabsTrigger>
                    </TabsList>

                    {/* Features Tab */}
                    <TabsContent value="features" className="space-y-3">
                      {Object.entries(groupedFeatures).map(
                        ([category, features]) => (
                          <CollapsibleSection
                            key={category}
                            title={
                              FEATURE_CATEGORIES[category]?.name || category
                            }
                            icon={FEATURE_CATEGORIES[category]?.icon || "📦"}
                            count={
                              features.filter((f) => formFeatures[f.key]).length
                            }
                            total={features.length}
                          >
                            <div className="space-y-2 pt-2">
                              {features.map((feature) => (
                                <div
                                  key={feature.key}
                                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50"
                                >
                                  <div>
                                    <div className="font-medium text-sm">
                                      {feature.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {feature.description}
                                    </div>
                                  </div>
                                  <Switch
                                    checked={formFeatures[feature.key] || false}
                                    onCheckedChange={() =>
                                      toggleFeature(feature.key)
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </CollapsibleSection>
                        ),
                      )}
                    </TabsContent>

                    {/* Limits Tab */}
                    <TabsContent value="limits" className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {options.limits.map((limit) => (
                          <div key={limit.key} className="space-y-2">
                            <Label className="text-sm font-medium">
                              {limit.name}
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                min="0"
                                placeholder={
                                  formLimits[limit.key] === -1
                                    ? t("common.unlimited")
                                    : `Default: ${limit.defaultValue}`
                                }
                                value={
                                  formLimits[limit.key] === -1
                                    ? ""
                                    : (formLimits[limit.key] ?? "")
                                }
                                onChange={(e) =>
                                  setLimit(limit.key, e.target.value)
                                }
                                // Stays editable while unlimited: typing a
                                // number is the other way back off -1.
                                disabled={isLimitLocked(limit)}
                                className="flex-1"
                              />
                              <Button
                                variant={
                                  formLimits[limit.key] === -1
                                    ? "default"
                                    : "outline"
                                }
                                size="icon"
                                disabled={isLimitLocked(limit)}
                                onClick={() => toggleUnlimited(limit.key)}
                                title={
                                  formLimits[limit.key] === -1
                                    ? "Unlimited — click to set a number"
                                    : "Set unlimited"
                                }
                              >
                                <InfinityIcon className="h-4 w-4" />
                              </Button>
                            </div>
                            {isLimitLocked(limit) && (
                              <p className="text-xs text-muted-foreground">
                                Locked — the{" "}
                                {options.features?.find(
                                  (f) => f.key === limit.requiresFeature,
                                )?.name || limit.requiresFeature}{" "}
                                feature is off
                              </p>
                            )}
                            {!isLimitLocked(limit) &&
                              formLimits[limit.key] === -1 && (
                                <p className="text-xs text-green-600">
                                  {t("common.unlimited")}
                                </p>
                              )}
                          </div>
                        ))}
                      </div>
                    </TabsContent>

                    <TabsContent value="pricing" className="space-y-4">
                      <div className="rounded-lg border p-3 text-sm">
                        <div className="font-medium">
                          {effectiveBillingType === "payg"
                            ? "Pay as you go — these rates are the invoice"
                            : "Fixed price — these rates apply to overage only"}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Rates are per unit, in AUD, and apply to this
                          organisation only. Anything left blank falls back to
                          the platform default. Changing a rate re-prices this
                          organisation&apos;s unbilled usage; already-invoiced
                          usage keeps the price it was billed at.
                        </p>
                      </div>
                      {selectedOrg?.id ? (
                        <UsagePricesEditor organisationId={selectedOrg.id} />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Save the plan first, then set per-unit rates.
                        </p>
                      )}
                    </TabsContent>

                    {/* Billing Tab */}
                    <TabsContent value="billing" className="space-y-4">
                      <div className="space-y-2">
                        <Label>Billing type</Label>
                        <Select
                          value={formBillingType}
                          onValueChange={setFormBillingType}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">
                              Fixed price — recurring fee for defined limits
                            </SelectItem>
                            <SelectItem value="payg">
                              Pay as you go — billed per unit of usage
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {formBillingType === "fixed" && hasUnlimitedLimit ? (
                          <p className="text-xs text-amber-600">
                            At least one limit is still unlimited, so this will
                            be saved as pay-as-you-go. Give every limit a number
                            on the Limits tab to charge a fixed price.
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Fixed price bills the fee below plus overage past
                            each limit. Pay-as-you-go bills only metered usage,
                            at the rates on the Pricing tab.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>{t("admin.customPlans.planName")}</Label>
                        <Input
                          placeholder={t(
                            "admin.customPlans.planNamePlaceholder",
                          )}
                          value={formPlanName}
                          onChange={(e) => setFormPlanName(e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t("admin.customPlans.price")}</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={formPrice}
                              onChange={(e) => setFormPrice(e.target.value)}
                              disabled={effectiveBillingType === "payg"}
                              className="pl-9"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>
                            {t("admin.customPlans.billingInterval")}
                          </Label>
                          <Select
                            value={formBillingInterval}
                            onValueChange={setFormBillingInterval}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="month">
                                {t("billing.plans.monthly")}
                              </SelectItem>
                              <SelectItem value="year">
                                {t("billing.plans.yearly")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{t("admin.customPlans.expiresAt")}</Label>
                          <DatePicker
                            value={formExpiresAt}
                            onChange={(e) => setFormExpiresAt(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t("admin.customPlans.notes")}</Label>
                        <Textarea
                          placeholder={t("admin.customPlans.notesPlaceholder")}
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t("admin.customPlans.changeReason")}</Label>
                        <Input
                          placeholder={t(
                            "admin.customPlans.changeReasonPlaceholder",
                          )}
                          value={formReason}
                          onChange={(e) => setFormReason(e.target.value)}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                )}

                {/* Save Button */}
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={closeDetails}>
                    {t("common.cancel")}
                  </Button>
                  <Button onClick={saveCustomPlan} disabled={saving}>
                    {saving ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
              <Settings2 className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg">{t("admin.selectOrgToConfigure")}</p>
              <p className="text-sm">{t("admin.createCustomPlan")}</p>
            </div>
          )}
        </Card>
      </div>

      {/* New Custom Plan Dialog */}
      <Dialog open={newPlanDialog} onOpenChange={setNewPlanDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.customPlans.createTitle")}</DialogTitle>
            <DialogDescription>
              {t("admin.customPlans.createDesc")}
            </DialogDescription>
          </DialogHeader>

          {/* Organisation Search */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("admin.customPlans.selectOrganization")}</Label>
              <Input
                placeholder={t("admin.organisations.searchPlaceholder")}
                value={searchOrg}
                onChange={(e) => setSearchOrg(e.target.value)}
              />
              {searchOrg && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {filteredOrgs.slice(0, 10).map((org) => (
                    <div
                      key={org.id}
                      className={cn(
                        "p-2 cursor-pointer hover:bg-muted",
                        targetOrg?.id === org.id && "bg-primary/10",
                      )}
                      onClick={() => setTargetOrg(org)}
                    >
                      <div className="font-medium">{org.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("admin.customPlans.currentPlan")}: {org.plan} •{" "}
                        {org.userCount || 0} {t("team.members")}
                      </div>
                    </div>
                  ))}
                  {filteredOrgs.length === 0 && (
                    <div className="p-4 text-center text-muted-foreground">
                      {t("admin.customPlans.noOrgsFound")}
                    </div>
                  )}
                </div>
              )}
              {targetOrg && (
                <div className="p-3 bg-primary/5 rounded-lg">
                  <div className="font-medium">{targetOrg.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {t("admin.customPlans.willBeConverted")}
                  </div>
                </div>
              )}
            </div>

            {targetOrg && (
              <Tabs defaultValue="features">
                <TabsList>
                  <TabsTrigger value="features">
                    {t("admin.customPlans.features")}
                  </TabsTrigger>
                  <TabsTrigger value="limits">
                    {t("admin.customPlans.limits")}
                  </TabsTrigger>
                  <TabsTrigger value="billing">
                    {t("billing.title")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="features"
                  className="max-h-60 overflow-y-auto"
                >
                  <div className="grid grid-cols-2 gap-2">
                    {options.features.map((feature) => (
                      <div
                        key={feature.key}
                        className="flex items-center justify-between p-2 rounded hover:bg-muted/50"
                      >
                        <span className="text-sm">{feature.name}</span>
                        <Switch
                          checked={formFeatures[feature.key] || false}
                          onCheckedChange={() => toggleFeature(feature.key)}
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="limits">
                  <div className="grid grid-cols-2 gap-4">
                    {options.limits.map((limit) => (
                      <div key={limit.key} className="space-y-1">
                        <Label className="text-xs">{limit.name}</Label>
                        <div className="flex gap-1">
                          <Input
                            type="number"
                            min="0"
                            placeholder={
                              formLimits[limit.key] === -1
                                ? t("common.unlimited")
                                : String(limit.defaultValue)
                            }
                            value={
                              formLimits[limit.key] === -1
                                ? ""
                                : (formLimits[limit.key] ?? "")
                            }
                            onChange={(e) =>
                              setLimit(limit.key, e.target.value)
                            }
                            disabled={isLimitLocked(limit)}
                            className="h-8"
                          />
                          <Button
                            variant={
                              formLimits[limit.key] === -1
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className="h-8 w-8 p-0"
                            disabled={isLimitLocked(limit)}
                            onClick={() => toggleUnlimited(limit.key)}
                            title={
                              formLimits[limit.key] === -1
                                ? "Unlimited — click to set a number"
                                : "Set unlimited"
                            }
                          >
                            <InfinityIcon className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="billing" className="space-y-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{t("billing.planName")}</Label>
                    <Input
                      placeholder="e.g., Enterprise Deal, Partner Plan..."
                      value={formPlanName}
                      onChange={(e) => setFormPlanName(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">{t("billing.priceUsd")}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formPrice}
                        onChange={(e) => setFormPrice(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">
                        {t("reminders.interval")}
                      </Label>
                      <Select
                        value={formBillingInterval}
                        onValueChange={setFormBillingInterval}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="month">
                            {t("billing.monthly")}
                          </SelectItem>
                          <SelectItem value="year">
                            {t("billing.yearly")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t("common.expires")}</Label>
                      <DatePicker
                        value={formExpiresAt}
                        onChange={(e) => setFormExpiresAt(e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t("common.notes")}</Label>
                    <Textarea
                      placeholder={t("placeholders.internalNotes")}
                      value={formNotes}
                      onChange={(e) => setFormNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPlanDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveCustomPlan} disabled={!targetOrg || saving}>
              {saving ? "Creating..." : "Create Custom Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("common.changeHistory")}</DialogTitle>
            <DialogDescription>
              History of changes for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>

          {renderHistoryContent()}
        </DialogContent>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <Dialog open={revertDialog} onOpenChange={setRevertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Revert to Standard Plan
            </DialogTitle>
            <DialogDescription>
              This will remove all custom plan configuration and switch{" "}
              {selectedOrg?.name} to a standard plan.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("billing.plans.newPlan")}</Label>
              <Select value={revertPlan} onValueChange={setRevertPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">{t("common.free")}</SelectItem>
                  <SelectItem value="starter">
                    {t("billing.plans.starter")}
                  </SelectItem>
                  <SelectItem value="professional">
                    {t("billing.plans.professional")}
                  </SelectItem>
                  <SelectItem value="enterprise">
                    {t("common.enterprise")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("common.reason")}</Label>
              <Input
                placeholder={t("placeholders.reasonForReverting")}
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevert}
              disabled={saving}
            >
              {saving ? "Reverting..." : "Revert Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
